import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { FileText, Download, Search, RefreshCw, ShieldCheck } from "lucide-react";

const TICKET_TYPES = [
  "Day Pass",
  "Weekend Pass",
  "Woman's Bunk Spot",
  "Queen Room",
  "King Master Suite",
];

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function WaiversTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [ticketFilter, setTicketFilter] = useState("");

  const fetchWaivers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (ticketFilter) params.ticket_type = ticketFilter;
      const res = await api.get("/admin/waivers", { params });
      setItems(res.data.items || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load waivers");
    } finally {
      setLoading(false);
    }
  }, [search, ticketFilter]);

  useEffect(() => {
    fetchWaivers();
  }, [fetchWaivers]);

  const downloadPdf = async (waiver) => {
    try {
      const res = await fetch(`${API}/admin/waivers/${waiver.id}/pdf`, { credentials: "include" });
      if (!res.ok) throw new Error("PDF download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `SSU-Waiver-${waiver.first_name}_${waiver.last_name}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("PDF download failed");
    }
  };

  const downloadCsv = async () => {
    try {
      const res = await fetch(`${API}/admin/waivers/export`, { credentials: "include" });
      if (!res.ok) throw new Error("CSV export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "waivers.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("CSV export failed");
    }
  };

  const counts = useMemo(() => {
    const byType = {};
    for (const w of items) {
      byType[w.ticket_type] = (byType[w.ticket_type] || 0) + 1;
    }
    return byType;
  }, [items]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="text-[10px] font-black tracking-[0.25em] uppercase text-[#0066FF] mb-1">Legal · Signed</div>
          <h2 className="font-display-tight text-2xl md:text-3xl text-[#0A1128] leading-tight">Liability Waivers</h2>
          <p className="text-sm text-[#6B7280] mt-1">
            Every signed waiver is stored here. Download any individual PDF or export the whole list as CSV.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchWaivers}
            data-testid="waivers-refresh"
            className="inline-flex items-center gap-1.5 border border-[#E5E7EB] hover:bg-[#F3F4F6] text-sm font-semibold text-[#0A1128] px-3 py-2 rounded-md"
          >
            <RefreshCw size={14} /> Refresh
          </button>
          <button
            onClick={downloadCsv}
            data-testid="export-waivers"
            className="inline-flex items-center gap-1.5 bg-[#0A1128] hover:bg-[#0F1B3D] text-white text-sm font-semibold px-3 py-2 rounded-md"
          >
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-[#E5E7EB] rounded-xl p-4 flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email…"
            data-testid="waivers-search"
            className="w-full pl-9 pr-3 py-2 border border-[#E5E7EB] rounded-md text-sm focus:outline-none focus:border-[#0066FF]"
          />
        </div>
        <select
          value={ticketFilter}
          onChange={(e) => setTicketFilter(e.target.value)}
          data-testid="waivers-ticket-filter"
          className="border border-[#E5E7EB] rounded-md text-sm px-3 py-2 focus:outline-none focus:border-[#0066FF]"
        >
          <option value="">All ticket types</option>
          {TICKET_TYPES.map((t) => (
            <option key={t} value={t}>{t}{counts[t] ? ` (${counts[t]})` : ""}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB] text-[#6B7280]">
              <tr>
                <Th>Signed</Th>
                <Th>Name</Th>
                <Th>Email</Th>
                <Th>Phone</Th>
                <Th>DOB</Th>
                <Th>Ticket</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody data-testid="waivers-tbody">
              {loading && (
                <tr>
                  <td colSpan={7} className="p-6 text-center text-[#6B7280]">Loading…</td>
                </tr>
              )}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-10 text-center">
                    <ShieldCheck size={28} className="mx-auto text-[#9CA3AF] mb-2" />
                    <div className="text-[#6B7280]">No waivers yet.</div>
                    <div className="text-xs text-[#9CA3AF] mt-1">
                      Share <code className="bg-[#F3F4F6] px-1 rounded">/waiver</code> with attendees to collect signatures.
                    </div>
                  </td>
                </tr>
              )}
              {!loading && items.map((w) => (
                <tr key={w.id} data-testid={`waiver-row-${w.id}`} className="border-b border-[#F3F4F6] hover:bg-[#FAFAF7]">
                  <Td>{fmt(w.created_at || w.submitted_at)}</Td>
                  <Td className="font-semibold text-[#0A1128]">{w.first_name} {w.last_name}</Td>
                  <Td>{w.email}</Td>
                  <Td>{w.phone}</Td>
                  <Td>{w.date_of_birth}</Td>
                  <Td><span className="inline-block px-2 py-0.5 text-xs font-semibold rounded border bg-[#EFF6FF] border-[#DBEAFE] text-[#1E40AF]">{w.ticket_type}</span></Td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => downloadPdf(w)}
                      data-testid={`waiver-pdf-${w.id}`}
                      className="inline-flex items-center gap-1.5 text-[#0066FF] hover:text-[#0052CC] text-sm font-semibold"
                    >
                      <FileText size={14} /> PDF
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`text-left font-semibold uppercase tracking-wider text-[10px] px-4 py-3 ${className}`}>{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-4 py-3 text-[#374151] ${className}`}>{children}</td>;
}
